import { useState } from "react";
import { Image as DatoImage, ResponsiveImageType } from "react-datocms";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { FixedAspectRatioContainer } from "@/components";
import { IMAGE_OBJECT_POSITION } from "@/constants";
import { AnchorButton, ButtonProps } from "@narrative-software/narrative-web-ui";
import { ACCOUNT_BASE_URL } from "../../config/env";
import { MODULE_KEYS } from "../../modules/config";

export type Media = {
  responsiveImage?: ResponsiveImageType;
  video?: {
    mp4Url: string;
    thumbnailUrl: string;
  };
};

type HeroFragmentType = {
  _modelApiKey: typeof MODULE_KEYS.heroV2;
  background: {
    responsiveImage: ResponsiveImageType;
  };
  headingLine1: string;
  headingLine2: string;
  headingMaxWidth?: number;
  description: string;
  ctaButtonText: string;
  ctaHref: string;
  ctaAppearance: ButtonProps["appearance"];
  ctaColor: ButtonProps["colour"];
  previewLinksText: string;
  previewMedia: Media[];
  videoThumbnails: Media[];
};

const heroFields = `
  _modelApiKey
  background {
    responsiveImage(imgixParams: { auto: format, w: 1600 }) {
      ...responsiveImageFragment
    }
  }
  headingLine1
  headingLine2
  headingMaxWidth
  description
  ctaButtonText
  ctaHref
  ctaAppearance
  ctaColor
  previewLinksText
  previewMedia {
    responsiveImage(imgixParams: { auto: format, w: 1200 }) {
      ...responsiveImageFragment
    }
    video {
      mp4Url(res: medium)
      thumbnailUrl
    }
  }
  videoThumbnails {
    responsiveImage(imgixParams: { auto: format, w: 1200 }) {
      ...responsiveImageFragment
    }
  }
`;

type HeroProps = HeroFragmentType & {
  prelude?: React.ReactNode;
};

function Hero({
  background,
  prelude,
  headingLine1,
  headingLine2,
  headingMaxWidth,
  description,
  ctaButtonText,
  ctaHref,
  ctaAppearance,
  ctaColor,
  previewLinksText,
  previewMedia,
  videoThumbnails,
}: HeroProps) {
  const [selectedIndex, setSelectedIndexRaw] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const setSelectedIndex = (index: number) => {
    setShowVideo(false);
    setSelectedIndexRaw(index);
  };

  const previewImage = previewMedia?.[selectedIndex]?.responsiveImage;
  const previewVideo = previewMedia?.[selectedIndex]?.video;
  const previewThumbnail = videoThumbnails?.[selectedIndex]?.responsiveImage;
  const paddingBottom = previewThumbnail?.aspectRatio ? (1 / previewThumbnail.aspectRatio) * 100 : 60;

  return (
    <section className="w-full pt-16 md:pt-38 relative bg-mankey">
      <div className="absolute top-0 right-0 left-0 h-[84%] md:h-[78%] bg-black">
        {background?.responsiveImage && (
          <DatoImage
            data={{
              ...background.responsiveImage,
              sizes: "(max-width: 768px) 100vw, 50vw",
            }}
            objectFit="cover"
            layout="fill"
            className="absolute top-0 right-0 bottom-0 left-0"
            priority
          />
        )}
      </div>
      <div className="m-auto max-w-[1050px] w-full text-center relative text-white px-3">
        {prelude}
        <div className="md:gap-7.5 gap-4 flex flex-col px-2">
          <h1
            className="md:text-90 font-[600] md:leading-[5.8rem] text-5xl leading-[3.25rem] mx-auto"
            style={{ maxWidth: headingMaxWidth }}
          >
            {headingLine1}
            <br />
            <span className="font-[300]">{headingLine2}</span>
          </h1>
          <div className="text-lg max-w-160 mx-auto" dangerouslySetInnerHTML={{ __html: description }} />
          <div className="relative max-w-[385px] w-full mx-auto lg:mb-20 mb-20">
            <AnchorButton
              appearance={ctaAppearance}
              colour={ctaColor}
              href={ctaHref?.replace("<account-site>", ACCOUNT_BASE_URL as string)}
            >
              {ctaButtonText}
            </AnchorButton>
          </div>
        </div>

        <div style={{ paddingBottom: `${paddingBottom}%` }} className="w-full h-0 relative bg-off-white mx-auto">
          <AnimatePresence exitBeforeEnter>
            <motion.div
              className="absolute top-0 right-0 bottom-0 left-0"
              key={selectedIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {previewImage && (
                <DatoImage
                  data={previewImage}
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    maxWidth: "10000px",
                  }}
                  objectFit="cover"
                  objectPosition={IMAGE_OBJECT_POSITION}
                  usePlaceholder={false}
                />
              )}
              {previewVideo && previewThumbnail && !showVideo && (
                <div onClick={() => setShowVideo(true)}>
                  <DatoImage
                    data={previewThumbnail}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      maxWidth: "10000px",
                    }}
                    objectFit="cover"
                    objectPosition={IMAGE_OBJECT_POSITION}
                    usePlaceholder={false}
                  />
                </div>
              )}
              {previewVideo && showVideo && (
                <FixedAspectRatioContainer
                  aspectRatio={previewThumbnail?.aspectRatio || 1192 / 725}
                  className={classNames("flex-grow w-full overflow-hidden lg:w-auto", {
                    // 'border border-cinder-block rounded shadow-md': product === 'select',
                  })}
                >
                  <video src={previewVideo.mp4Url} autoPlay playsInline loop={false} controls={true} />
                </FixedAspectRatioContainer>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {previewLinksText?.trim() && (
          <div className="flex mt-5 justify-center flex-wrap">
            {previewLinksText
              .split(",")
              .slice(0, previewMedia?.length)
              .map((linkText, i) => (
                <button
                  key={linkText}
                  className={classNames("text-stealth-bomber px-2 md:px-5 py-1 transition-colors", {
                    underline: selectedIndex === i,
                    "text-shady-lady": selectedIndex !== i,
                  })}
                  onClick={() => setSelectedIndex(i)}
                >
                  {linkText.trim()}
                </button>
              ))}
          </div>
        )}
      </div>
    </section>
  );
}

export type { HeroFragmentType };
export { Hero, heroFields };
